import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase';
import { Mail, MessageSquare, Send, Calendar, CheckCircle } from 'lucide-react';
import PublicNavbar from '../../components/PublicNavbar';
import Footer from '../../components/Footer';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, 'contactMessages'), {
        ...form,
        product: 'buildmyshifts',
        createdAt: new Date().toISOString(),
      });
      setSent(true);
    } catch (err) {
      console.error('Contact form error:', err);
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-white">
        <PublicNavbar />
        <div className="flex items-center justify-center px-4 py-32">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={36} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{t('contact.sent', 'Message Sent!')}</h1>
            <p className="text-gray-600">
              {t('contact.sentDesc', "Thank you for reaching out. We'll get back to you within 24 hours.")}
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <div className="max-w-3xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('contact.title', 'Get in Touch')}</h1>
          <p className="text-lg text-gray-600">
            {t('contact.subtitle', "Have a question or need help? We'd love to hear from you.")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: Mail, label: t('contact.email', 'Email'), value: 'support@buildmyshifts.com' },
            { icon: MessageSquare, label: t('contact.responseTime', 'Response Time'), value: t('contact.within24', 'Within 24 hours') },
            { icon: Calendar, label: t('contact.hours', 'Hours'), value: t('contact.hoursValue', 'Mon–Fri 9am–5pm GMT') },
          ].map((item, i) => (
            <div key={i} className="text-center p-5 bg-gray-50 rounded-xl">
              <item.icon size={24} className="text-primary mx-auto mb-2" />
              <p className="font-medium text-gray-900">{item.label}</p>
              <p className="text-sm text-gray-600">{item.value}</p>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.name', 'Name')}</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.emailLabel', 'Email')}</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.subject', 'Subject')}</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.message', 'Message')}</label>
            <textarea
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={18} /> {loading ? t('contact.sending', 'Sending...') : t('contact.send', 'Send Message')}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
